// src/features/fiscalization/RegisterFiscalPointModal.tsx
// Registers a cash register / fiscal device with a fiscal provider.
// Covers: RegisterFiscalPoint.

import { useEffect, useState } from 'react';
import { useI18n, useFiscalizationFiscalProvider } from '@react/hooks';
import { Button, Input } from '@react/components';
import { Select } from '@/components/Select';
import type { TaxationSystem } from '@core/types/fiscalization';
import { TaxationSystemValues } from '@core/types/fiscalization';
import { errMessage, humanizeEnum } from './format';

const TAXATION_OPTIONS = TaxationSystemValues.filter((v) => v !== 'TAXATION_SYSTEM_UNSPECIFIED');

export interface RegisterFiscalPointValue {
  name: string;
  description: string;
  provider_id: string;
  registration_number: string;
  serial_number: string;
  fiscal_drive_serial: string;
  tax_authority_id: string;
  taxation_system: TaxationSystem;
}

export interface RegisterFiscalPointModalProps {
  open: boolean;
  isSaving?: boolean;
  onCancel: () => void;
  onSubmit: (value: RegisterFiscalPointValue) => Promise<void>;
}

export function RegisterFiscalPointModal({ open, isSaving, onCancel, onSubmit }: RegisterFiscalPointModalProps) {
  const { t } = useI18n();
  const providerStore = useFiscalizationFiscalProvider();
  const [form, setForm] = useState<RegisterFiscalPointValue>({
    name: '', description: '', provider_id: '', registration_number: '', serial_number: '',
    fiscal_drive_serial: '', tax_authority_id: '', taxation_system: TAXATION_OPTIONS[0],
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && providerStore.items.length === 0) {
      providerStore.ListFiscalProviders({ pagination: { cursor: '', limit: 100, direction: 'forward' }, sort: [], date_range: {} as any, filters: {} as any } as any);
    }
  }, [open]);

  if (!open) return null;

  const change = <K extends keyof RegisterFiscalPointValue>(field: K, value: RegisterFiscalPointValue[K]) => setForm((p) => ({ ...p, [field]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await onSubmit(form);
    } catch (err) {
      setError(errMessage(err, t('fiscalization.points.saveError')));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto py-8">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onCancel} aria-hidden="true" />
      <form onSubmit={handleSubmit} role="dialog" aria-modal="true" className="relative mx-4 w-full max-w-lg rounded-lg border border-border bg-background p-5 shadow-xl space-y-4">
        <h3 className="text-base font-semibold text-foreground">{t('fiscalization.points.register')}</h3>
        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="grid grid-cols-2 gap-3">
          <Input label={t('common.name')} value={form.name} onChange={(e) => change('name', e.target.value)} required />
          <Select label={t('fiscalization.points.provider')} value={form.provider_id} onChange={(e) => change('provider_id', e.target.value)} required>
            <option value="">{t('common.select')}</option>
            {providerStore.items.map((p) => (
              <option key={p.entity?.id} value={p.entity?.id}>{p.name}</option>
            ))}
          </Select>
        </div>
        <Input label={t('common.description')} value={form.description} onChange={(e) => change('description', e.target.value)} />
        <div className="grid grid-cols-2 gap-3">
          <Input label={t('fiscalization.points.registrationNumber')} value={form.registration_number} onChange={(e) => change('registration_number', e.target.value)} required />
          <Input label={t('fiscalization.points.serialNumber')} value={form.serial_number} onChange={(e) => change('serial_number', e.target.value)} required />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label={t('fiscalization.points.fiscalDriveSerial')} value={form.fiscal_drive_serial} onChange={(e) => change('fiscal_drive_serial', e.target.value)} />
          <Input label={t('fiscalization.points.taxAuthorityId')} value={form.tax_authority_id} onChange={(e) => change('tax_authority_id', e.target.value)} />
        </div>
        <Select label={t('fiscalization.points.taxationSystem')} value={form.taxation_system} onChange={(e) => change('taxation_system', e.target.value as TaxationSystem)}>
          {TAXATION_OPTIONS.map((v) => (
            <option key={v} value={v}>{humanizeEnum(v, 'TAXATION_SYSTEM_')}</option>
          ))}
        </Select>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onCancel} disabled={isSaving} className="rounded-md px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted disabled:opacity-50">
            {t('common.cancel')}
          </button>
          <Button type="submit" size="sm" isLoading={isSaving}>{t('fiscalization.points.register')}</Button>
        </div>
      </form>
    </div>
  );
}

export default RegisterFiscalPointModal;
